import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { readSnapshot, writeSnapshot, clearSnapshots, estErreurReseau } from '../services/offline';
import { clearMsalCache } from '../services/sso';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/auth/me')
        .then(({ data }) => { setUser(data.data); writeSnapshot('me', data.data); })
        .catch((error) => {
          // Hors ligne, on repart du dernier profil connu : le serveur n'a pas
          // rejeté la session, il n'a simplement pas pu être joint. Effacer le
          // token ici déconnectait l'utilisateur pour de bon dès la moindre
          // coupure — c'est ce qui rendait la PWA inutilisable sans réseau.
          const instantane = estErreurReseau(error) ? readSnapshot('me') : null;
          if (instantane) { setUser(instantane.data); return; }
          localStorage.removeItem('token');
          clearSnapshots();
          delete api.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Les réponses de /auth/login et /auth/sso ne portent pas les groupes :
  // on relit /auth/me pour disposer du même profil complet que sur un
  // rechargement de page (groupes compris).
  const applySession = async ({ token, user: userData }) => {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.data);
      writeSnapshot('me', data.data);
      return data.data;
    } catch {
      setUser(userData);
      writeSnapshot('me', userData);
      return userData;
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    return applySession(data.data);
  };

  const loginSso = (sessionData) => applySession(sessionData);

  const logout = () => {
    localStorage.removeItem('token');
    clearSnapshots();
    delete api.defaults.headers.common['Authorization'];
    // Sans attendre : la sortie de l'utilisateur ne doit pas dépendre du
    // nettoyage du cache MSAL, qui peut échouer sans conséquence.
    clearMsalCache();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginSso, logout, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
